import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { useSEO } from "@/lib/useSEO";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fireAdsPurchase } from "@/lib/ads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { BookOpen, Download, CheckCircle2, Lock, ArrowLeft, Tag, Clock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { SignInRequiredGate } from "@/components/SignInRequiredGate";

export default function LibraryDetail() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [resource, setResource] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", goal: "" });
  const [submitting, setSubmitting] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [buying, setBuying] = useState(false);
  const paymentStatus = params.get("payment"); // success | cancel

  useEffect(() => {
    if (paymentStatus === "success") {
      fireAdsPurchase({ transactionId: params.get("session_id") });
    }
  }, [paymentStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  useSEO({
    title: resource ? `${resource.title} — Better Through Boxing Library` : "Boxing PDF — Better Through Boxing",
    description: resource?.description || "Downloadable boxing guides from Coach Chris Persaud.",
    path: `/library/${slug}`,
  });

  // Pre-fill name/email when the user is signed in so they don't retype
  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        full_name: f.full_name || user.name || "",
        email: f.email || user.email || "",
      }));
    }
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/library/${slug}`);
        if (!cancelled) setResource(data);
      } catch {
        if (!cancelled) setNotFound(true);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

  const requestFree = async (e) => {
    e?.preventDefault?.();
    if (!form.full_name.trim() || !form.email.trim()) {
      toast.error("Name and email required");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/library/${slug}/request-free`, form);
      setDownloadUrl(data?.download_url || null);
      toast.success("Check your inbox — also try the download button below.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const buyPaid = async () => {
    if (!form.full_name.trim() || !form.email.trim()) {
      toast.error("Add your name and email first");
      return;
    }
    setBuying(true);
    try {
      const { data } = await api.post(`/library/${slug}/buy`, {
        full_name: form.full_name, email: form.email,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBuying(false);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#050505] text-white">
        <SiteHeader />
        <div className="max-w-4xl mx-auto px-6 py-24 text-center">
          <h1 className="font-display text-4xl uppercase text-white mb-4">Resource not found</h1>
          <Link to="/library" className="text-[#FF331F] font-bold uppercase tracking-widest text-sm">← Back to the library</Link>
        </div>
        <SiteFooter />
      </div>
    );
  }
  if (!resource) {
    return (
      <div className="min-h-screen bg-[#050505] text-white">
        <SiteHeader />
        <div className="max-w-4xl mx-auto px-6 py-24"><div className="h-64 bg-[#0A0A0A] animate-pulse" /></div>
        <SiteFooter />
      </div>
    );
  }

  const isFree = resource.is_currently_free;
  const price = resource.price_cents > 0 ? `$${(resource.price_cents / 100).toFixed(2)}` : "Free";

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="library-detail">
      <SiteHeader />

      <article className="max-w-6xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <Link to="/library" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-zinc-500 hover:text-[#FF331F] font-bold mb-6">
          <ArrowLeft className="h-3 w-3" /> All downloads
        </Link>

        {paymentStatus === "success" && (
          <div className="mb-6 border border-emerald-700 bg-emerald-900/20 p-4 rounded-sm flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 text-emerald-400 mt-0.5" />
            <div>
              <p className="text-emerald-300 font-bold uppercase tracking-widest text-xs mb-1">Purchase complete</p>
              <p className="text-sm text-zinc-300">Check your email — your download link was just sent. You can also access it anytime from your <Link to="/portal" className="text-[#FF331F] underline">client portal</Link>.</p>
            </div>
          </div>
        )}
        {paymentStatus === "cancel" && (
          <div className="mb-6 border border-amber-700 bg-amber-900/20 p-4 rounded-sm text-sm text-amber-200">
            Checkout cancelled — no charge made. Come back when ready.
          </div>
        )}

        <div className="grid md:grid-cols-5 gap-8 md:gap-12">
          <div className="md:col-span-2">
            <div className="aspect-[4/5] bg-[#0A0A0A] border border-[#27272A] overflow-hidden">
              {resource.cover_image ? (
                <img src={resource.cover_image} alt={resource.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center"><BookOpen className="h-20 w-20 text-zinc-700" /></div>
              )}
            </div>
            <div className="mt-4 flex items-center gap-3 text-[11px] uppercase tracking-widest text-zinc-500 font-bold">
              <span className="inline-flex items-center gap-1"><Tag className="h-3 w-3" /> {resource.category || "Guide"}</span>
              <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> PDF</span>
            </div>
          </div>

          <div className="md:col-span-3">
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#FF331F] font-bold mb-2">{price}</p>
            <h1 className="font-display text-4xl sm:text-5xl text-white uppercase tracking-tight leading-tight mb-4" data-testid="library-detail-title">
              {resource.title}
            </h1>
            <p className="text-base text-zinc-400 leading-relaxed mb-8 whitespace-pre-line">{resource.description}</p>

            {downloadUrl ? (
              <div className="border border-emerald-700 bg-emerald-900/15 p-6 rounded-sm" data-testid="library-success-block">
                <CheckCircle2 className="h-10 w-10 text-emerald-400 mb-3" />
                <h3 className="font-display text-2xl text-white uppercase tracking-tight mb-2">You're in.</h3>
                <p className="text-sm text-zinc-300 mb-5">We just emailed you the download link too. Tap below to grab it now.</p>
                <a href={`${apiBase}${downloadUrl}`} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-[0.2em] text-xs px-6 py-3 rounded-sm"
                  data-testid="library-download-btn"
                >
                  <Download className="h-4 w-4" /> Download the PDF
                </a>
              </div>
            ) : !user ? (
              <SignInRequiredGate
                title={isFree ? "Sign in to download this guide" : `Sign in to buy this guide (${price})`}
                subtitle="Every Better Through Boxing download lives in your account so you can find it again any time. Free to sign up."
                testid="library-signin-gate"
              />
            ) : isFree ? (
              <form onSubmit={requestFree} className="border border-[#27272A] bg-[#0A0A0A] p-6 rounded-sm space-y-3" data-testid="library-free-form">
                <p className="text-[11px] uppercase tracking-[0.25em] text-[#FF331F] font-bold">Free download — instant</p>
                <h3 className="font-display text-xl text-white uppercase tracking-tight mb-2">Tell us where to send it</h3>
                <Input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  placeholder="Your name" className="bg-[#050505] border-[#27272A] text-white rounded-sm h-11"
                  data-testid="library-name" />
                <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Email address" className="bg-[#050505] border-[#27272A] text-white rounded-sm h-11"
                  data-testid="library-email" />
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="Phone (optional)" className="bg-[#050505] border-[#27272A] text-white rounded-sm h-11"
                  data-testid="library-phone" />
                <Textarea value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })}
                  placeholder="What's your #1 goal with boxing? (optional)" className="bg-[#050505] border-[#27272A] text-white rounded-sm min-h-[70px]"
                  data-testid="library-goal" />
                <Button type="submit" disabled={submitting}
                  className="w-full bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-[0.2em] text-xs rounded-sm h-12"
                  data-testid="library-submit"
                >
                  {submitting ? "Sending…" : "Send Me The Download"}
                </Button>
                <p className="text-[10px] text-zinc-600 text-center">You'll also be added to a short 14-day email series from Coach Chris. Unsubscribe anytime.</p>
              </form>
            ) : (
              <div className="border border-[#27272A] bg-[#0A0A0A] p-6 rounded-sm space-y-3">
                <div className="flex items-center gap-2 text-[#FF331F] mb-2"><Lock className="h-4 w-4" /><p className="text-[11px] uppercase tracking-[0.25em] font-bold">Paid resource</p></div>
                <h3 className="font-display text-2xl text-white uppercase tracking-tight">Buy {resource.title} — {price}</h3>
                <p className="text-sm text-zinc-400">Secure checkout via Stripe. Instant download link + lifetime portal access.</p>
                <Input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  placeholder="Your name" className="bg-[#050505] border-[#27272A] text-white rounded-sm h-11"
                  data-testid="library-paid-name" />
                <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Email address" className="bg-[#050505] border-[#27272A] text-white rounded-sm h-11"
                  data-testid="library-paid-email" />
                <Button onClick={buyPaid} disabled={buying}
                  className="w-full bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-[0.2em] text-xs rounded-sm h-12"
                  data-testid="library-buy-btn">
                  {buying ? "Redirecting…" : `Buy for ${price}`}
                </Button>
              </div>
            )}
          </div>
        </div>
      </article>

      <SiteFooter />
    </div>
  );
}
